import { isPricingActiveOnDate, parseLocalDateString } from './dateUtils';

export const SPECIALIZATION_TO_SERVICE = {
  'Speech Therapy': 'ST',
  'Occupational Therapy': 'OT',
  'Physical Therapy': 'PT',
  'Play Group': 'PG',
};

export const SERVICE_TYPE_LABELS = {
  ST: 'Speech Therapy',
  OT: 'Occupational Therapy',
  PT: 'Physical Therapy',
  PG: 'Play Group',
  FT: 'Feeding Therapy',
};

export const BILLING_SERVICE_TYPES = ['ST', 'OT', 'PT', 'PG', 'FT'];

export const FEE_TYPES = {
  REGULAR: 'regular',
  FEEDING: 'feeding',
  SUNDAY: 'sunday',
  SUNDAY_FEEDING: 'sunday_feeding',
};

export function isSundayDate(dateStr) {
  if (!dateStr) {
    return false;
  }

  return parseLocalDateString(dateStr).getDay() === 0;
}

export function getBillingRateDate(attendanceDate, sessionLike) {
  if (!sessionLike) {
    return attendanceDate;
  }

  const isMakeup = sessionLike.is_makeup ?? sessionLike.session?.is_makeup;
  const makeupFor = sessionLike.makeup_for ?? sessionLike.session?.makeup_for;

  if (isMakeup && makeupFor) {
    return makeupFor;
  }

  return attendanceDate;
}

/** Open-ended recurring slot (not makeup or a one-day session). Future start dates included. */
export function isPermanentClientSession(session) {
  if (!session) {
    return false;
  }

  if (session.is_makeup) {
    return false;
  }

  const end = session.valid_until;
  if (end != null && end !== '') {
    return false;
  }

  return true;
}

export function getFeeTypeForBillingService(billingServiceType) {
  return billingServiceType === 'FT' ? FEE_TYPES.FEEDING : FEE_TYPES.REGULAR;
}

export function getFeeTypeForSession(billingServiceType, dateStr) {
  if (!isSundayDate(dateStr)) {
    return getFeeTypeForBillingService(billingServiceType);
  }

  return billingServiceType === 'FT' ? FEE_TYPES.SUNDAY_FEEDING : FEE_TYPES.SUNDAY;
}

export function getDefaultBillingServiceType(therapistSpecialization) {
  return SPECIALIZATION_TO_SERVICE[therapistSpecialization] || 'OT';
}

export function getBillingServiceType(attendance, therapistSpecialization) {
  if (attendance?.billing_service_type) {
    return attendance.billing_service_type;
  }

  return getDefaultBillingServiceType(therapistSpecialization);
}

export function getClientBillingRate(service, dateStr) {
  if (!service) {
    return 0;
  }

  const baseRate = Number(service.base_rate) || 0;

  if (
    isSundayDate(dateStr) &&
    !service.sunday_regular_rate &&
    service.sunday_rate !== null &&
    service.sunday_rate !== undefined &&
    service.sunday_rate !== ''
  ) {
    return Number(service.sunday_rate);
  }

  return baseRate;
}

export function findMatchingClientService(
  clientServices,
  therapistSpecialization,
  selectedDateStr,
  serviceTypeOverride = null
) {
  const targetServiceType =
    serviceTypeOverride || getDefaultBillingServiceType(therapistSpecialization);

  return clientServices?.find((service) => {
    const serviceMatches = service.service_type === targetServiceType;
    return serviceMatches && isPricingActiveOnDate(service, selectedDateStr);
  });
}

export function resolveBillingServiceType(context, therapist, clientServices, selectedDateStr) {
  const explicit =
    context?.billing_service_type ??
    context?.attendance?.billing_service_type ??
    context?.session?.billing_service_type;

  if (explicit) {
    return explicit;
  }

  const defaultType = getDefaultBillingServiceType(therapist?.specialization);

  if (canOfferFeedingTherapy(therapist, clientServices, selectedDateStr)) {
    const defaultService = findMatchingClientService(
      clientServices,
      therapist?.specialization,
      selectedDateStr,
      defaultType
    );

    if (!defaultService) {
      return 'FT';
    }
  }

  return defaultType;
}

export function clientHasFeedingTherapyService(clientServices, selectedDateStr) {
  return Boolean(
    findMatchingClientService(clientServices, 'Occupational Therapy', selectedDateStr, 'FT')
  );
}

export function canOfferFeedingTherapy(therapist, clientServices, selectedDateStr) {
  return (
    therapist?.specialization === 'Occupational Therapy' &&
    therapist?.feeding_therapy_certified === true &&
    clientHasFeedingTherapyService(clientServices, selectedDateStr)
  );
}

export function getActiveServiceFee(fees, feeType = FEE_TYPES.REGULAR) {
  return fees?.find(
    (fee) => !fee.valid_until && (fee.fee_type || FEE_TYPES.REGULAR) === feeType
  );
}
